import { normalizarPagamentoExterior, fornecedorAccessInterno } from './pagamentoExteriorPolicy';
import { FeeIntelligence } from './feeIntelligence';
import { AgenciaOrcamentoPolicy } from './agenciaOrcamentoPolicy';
import { identificarLinhaOrcamento } from './linhaOrcamentoIdentificacaoPolicy';
import { cambioBrlLinha, moedaDaLinhaFornecedor, temCambioProprio } from './moedaFornecedorPolicy';
import { isCoordenacaoRemota } from './coordenacaoRemotaPolicy';
import { ehSomenteVisualizacao } from './somenteVisualizacaoPolicy';

export const ETAPAS_REMESSA = { pre: 'PreEvento', neg: 'Negociado' };
const ausente = (valor) => valor === undefined || String(valor).trim() === '';
const numero = (valor) => {
    if (valor === null || ausente(valor)) return null;
    const texto = String(valor).trim();
    const convertido = Number(texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto);
    return Number.isFinite(convertido) ? convertido : null;
};
export const arredondarBrl = (valor) => Math.round((valor + Number.EPSILON) * 100) / 100;

// Moeda do fornecedor (27/09): linha com câmbio próprio usa a moeda/câmbio DELA em cada etapa
// (08/10: a moeda DA ETAPA; padrão Pré-evento); legado (sem câmbio próprio) segue o contexto global
// (moeda/câmbio do orçamento), como sempre.
export function moedaDaLinha(linha = {}, contexto = {}, etapa = 'pre') {
    return moedaDaLinhaFornecedor(linha, contexto.moeda, etapa);
}

export function cambioDaLinha(linha = {}, contexto = {}, etapa = 'pre') {
    if (temCambioProprio(linha)) return cambioBrlLinha(linha, etapa, 1);
    return String(contexto.moeda ?? 'real').toLowerCase() === 'real' ? 1 : numero(contexto.cambio);
}

// Regra ÚNICA do valor do orçamento no painel; a barra TOTAL ACCESS do orçamento internacional usa a
// mesma função sobre os números da tela (valorPainelOrcamentoPolicy, item 12).
// A API escolhe a coluna vigente no agregado (última preenchida). Não converter o total BRL
// novamente; fechamento concluído usa realizado e fechamento aberto/parcial
// mantém a estimativa comercial mais recente no Total Atual.
export function valorRemessaExteriorPainel(orcamento = {}) {
    const real = numero(orcamento.totalRealAtualBRL);
    if (orcamento.fechamento_real_ativo_atual === true && real !== null) return {
        valor: real, estimativaBrl: false, realizadoBrl: true, completo: orcamento.fechamento_real_completo === true, valorBrl: true, fonte: 'real_atual',
    };
    const estimado = numero(orcamento.totalEstimadoAtualBRL);
    const ativo = orcamento.remessa_estimativa_ativa_atual === true;
    if (ativo && estimado !== null) return { valor: estimado, estimativaBrl: true, valorBrl: true, fonte: 'estimativa_atual' };

    const moeda = ['real', 'dolar', 'euro', 'iene', 'libra', 'dolarcad'].includes(String(orcamento.moeda || '').toLowerCase())
        ? String(orcamento.moeda).toLowerCase() : 'real';
    const valorOriginal = numero(orcamento.valorTotal) ?? 0;
    if (moeda === 'real') return { valor: valorOriginal, estimativaBrl: false, valorBrl: true, fonte: 'valor_total' };

    const cambio = numero(orcamento.cambio);
    // Contrato 02/10 (regra 3): com o R$ exato do valorTotal (soma dos R$ das linhas; a tela calcula, a
    // API pode devolver `valorTotalBRL`), ele vale; sem ele, o legado valorTotal × câmbio.
    const valorBrl = numero(orcamento.valorTotalBRL);
    if (valorBrl !== null && cambio !== null && cambio > 0) return { valor: arredondarBrl(valorBrl), estimativaBrl: false, valorBrl: true, fonte: 'valor_total' };
    if (cambio !== null && cambio > 0) return {
        valor: arredondarBrl(valorOriginal * cambio), estimativaBrl: false, valorBrl: true, fonte: 'valor_total',
    };
    return { valor: valorOriginal, estimativaBrl: false, valorBrl: false, cambioPendente: true, moedaOriginal: moeda, fonte: 'valor_total' };
}

// Valor do painel na moeda do orçamento (round 3 item C): o R$ do painel (com remessa do exterior) ÷
// câmbio, arredondado uma vez; sem R$ (câmbio pendente) ou em Real, o valorTotal. Só exibição.
export function valorPainelNaMoedaOrcamento(valorPainel = {}, orcamento = {}) {
    const moeda = String(orcamento.moeda || 'real').toLowerCase();
    const cambio = numero(orcamento.cambio);
    if (moeda !== 'real' && valorPainel.valorBrl && cambio !== null && cambio > 0) return arredondarBrl(numero(valorPainel.valor) / cambio);
    return numero(orcamento.valorTotal) ?? 0;
}

// Etiquetas de status do valor do painel: mesmos textos na listagem (PainelOrcamento) e na barra
// TOTAL ACCESS do orçamento (item 12). `valor` = retorno de valorRemessaExteriorPainel.
export function etiquetasValorPainel(valor = {}, orcamento = {}) {
    const etiquetas = [];
    if (valor.realizadoBrl) etiquetas.push({ texto: valor.completo ? 'reconciliado BRL' : 'PARCIAL · conferência BRL', alerta: false });
    if (orcamento.fechamento_real_ativo && !valor.realizadoBrl) etiquetas.push({ texto: 'PARCIAL · pagamentos informados', alerta: true });
    if (valor.estimativaBrl) etiquetas.push({ texto: 'estimativa BRL', alerta: false, title: 'Total estimado em reais com encargos do pagamento no exterior; não convertido novamente.' });
    if (valor.cambioPendente) etiquetas.push({ texto: 'câmbio pendente', alerta: true });
    return etiquetas;
}

export const simboloMoedaOrcamento = (moeda) => ({ real: 'R$', dolar: 'US$', euro: '€', iene: '¥', libra: '£', dolarcad: 'C$' }[String(moeda || '').toLowerCase()] || '');

// Defaults são de exibição/cálculo: ler o legado não preenche nem muda o payload.
export function configuracaoRemessaEtapa(linha = {}, etapa = 'pre') {
    const config = linha.remessa_estimativa?.[etapa] ?? {};
    return {
        quantidade_envios: numero(config.quantidade_envios),
        taxa_envio_brl: ausente(config.taxa_envio_brl) ? 50 : numero(config.taxa_envio_brl),
        percentual_remessa: ausente(config.percentual_remessa) ? 12.5 : numero(config.percentual_remessa),
    };
}

// Ajuste manual do Imposto NF Access por etapa (reunião Inter 19/10), espelho de OrcamentoImpostoNfAjuste (API).
// Gravado em remessa_estimativa.nf_ajuste = { pre|neg|fech: { percentual, modo: 'dentro'|'direto', ajustado_em } };
// etapa ausente = 19,05% por dentro (padrão). Projetos antigos fechados com outra alíquota ou com a conta direta.
export const ETAPAS_NF_AJUSTE = ['pre', 'neg', 'fech'];
export const NF_ACCESS_PADRAO = { percentual: 19.05, modo: 'dentro' };
export function ajusteNfEtapa(linha = {}, etapa = 'pre') {
    const ajuste = linha.remessa_estimativa?.nf_ajuste?.[etapa];
    const percentual = numero(ajuste?.percentual);
    if (!ajuste || percentual === null || percentual < 0 || percentual >= 100) return null;
    return { percentual, modo: ajuste.modo === 'direto' ? 'direto' : 'dentro', ajustado_em: ajuste.ajustado_em ?? null };
}
export function calcularImpostoNfAccess(base, ajuste = null) {
    if (!(base > 0)) return 0;
    const { percentual, modo } = ajuste || NF_ACCESS_PADRAO;
    const aliquota = percentual / 100;
    return modo === 'direto' ? base * aliquota : base * aliquota / (1 - aliquota);
}
export const camposAjusteNf = (ajuste) => ({
    nf_percentual: (ajuste || NF_ACCESS_PADRAO).percentual, nf_modo: (ajuste || NF_ACCESS_PADRAO).modo,
    nf_ajustado: !!ajuste, nf_ajustado_em: ajuste?.ajustado_em ?? null,
});
// `ajuste` null desfaz a etapa (volta ao padrão); sem nenhuma etapa, a chave sai do objeto.
export function alterarAjusteNfLinha(orcamento, grupoIndex, linhaIndex, etapa, ajuste) {
    if (!ETAPAS_NF_AJUSTE.includes(etapa)) throw new Error('Etapa do ajuste do Imposto NF inválida.');
    return { ...orcamento, orcamentoitem: orcamento.orcamentoitem.map((grupo, g) => g !== grupoIndex ? grupo : {
        ...grupo, subitem: grupo.subitem.map((linha, l) => {
            if (l !== linhaIndex) return linha;
            const nfAjuste = { ...(linha.remessa_estimativa?.nf_ajuste || {}) };
            if (ajuste) nfAjuste[etapa] = { ...nfAjuste[etapa], percentual: numero(ajuste.percentual), modo: ajuste.modo === 'direto' ? 'direto' : 'dentro' };
            else delete nfAjuste[etapa];
            const remessa = { ...(linha.remessa_estimativa || {}) };
            if (Object.keys(nfAjuste).length) remessa.nf_ajuste = nfAjuste; else delete remessa.nf_ajuste;
            return { ...linha, remessa_estimativa: remessa };
        }),
    }) };
}

export function etapaEstimativaAtual(linha = {}) {
    if (numero(linha.totalNegociado) > 0) return 'neg';
    if (numero(linha.totalPreEvento) > 0) return 'pre';
    return null;
}

export function linhaElegivelRemessaExterior(linha = {}, fornecedorAccess = null) {
    let exterior;
    try { exterior = normalizarPagamentoExterior(linha.pagamento_exterior) === true; } catch (error) { return false; }
    const codigo = String(linha.produto?.codigo ?? '').trim().toUpperCase();
    return exterior && !['1945', 'IMPOSTO_NF_ACCESS'].includes(codigo)
        && !linha.linhaVirtualImpostoNfAccess && !linha.linhaCalculadaImpostoAccess
        && !FeeIntelligence.isFeeCalculatorItem(linha.produto ?? {})
        && !AgenciaOrcamentoPolicy.isFeeAgenciaGlobal(linha.produto ?? {})
        && !fornecedorAccessInterno(linha.fornecedor, fornecedorAccess)
        && !isCoordenacaoRemota(linha.produto);
}

// Contrato v2 "igual à planilha" (Rafael 02/10, noite):
// - A: IOF/IR = % × principal (0 sem principal); envio = envios × R$/envio SEMPRE que houver envios
//   (mesmo com principal 0, como o "Mimos" da planilha ASH); NF Access = 19,05% por fora sobre IOF/IR + envio.
// - B (`exato: true`): sem arredondar nada (principal = total × câmbio; IOF/IR, NF e adicional com todas as
//   casas), como a planilha; só a exibição arredonda. É o caminho de toda conta em R$ (linhas ↳, taxas do
//   grupo, TOTAL ACCESS, painel, Resumo, base do FEE em R$). Sem `exato` (padrão) mantém os centavos por
//   passo: base do FEE internacional do LEGADO (feeBaseInternacional) e o shadow do fechamento real — o
//   gravado de orçamento antigo não muda.
export function calcularRemessaExteriorEstimativa(linha = {}, contexto = {}, fornecedorAccess = null, { exato = false } = {}) {
    if (!linhaElegivelRemessaExterior(linha, fornecedorAccess)) return { elegivel: false };
    const resultado = { elegivel: true };
    const arred = exato ? (valor) => valor : arredondarBrl;
    Object.entries(ETAPAS_REMESSA).forEach(([etapa, momento]) => {
        const moedaLinha = moedaDaLinha(linha, contexto, etapa);
        const cambio = cambioDaLinha(linha, contexto, etapa);
        const config = configuracaoRemessaEtapa(linha, etapa);
        const principalOriginal = arred(numero(linha[`total${momento}`]) || 0);
        const principal = arred(principalOriginal * (cambio > 0 ? cambio : 0));
        const quantidadeValida = config.quantidade_envios !== null && Number.isInteger(config.quantidade_envios) && config.quantidade_envios >= 0;
        const taxaValida = config.taxa_envio_brl !== null && config.taxa_envio_brl >= 0;
        const percentualValido = config.percentual_remessa !== null && config.percentual_remessa >= 0;
        const remessa = principal > 0 && percentualValido ? arred(principal * config.percentual_remessa / 100) : 0;
        const envio = quantidadeValida && taxaValida ? arred(config.quantidade_envios * config.taxa_envio_brl) : 0;
        const ajusteNf = ajusteNfEtapa(linha, etapa);
        const nf = arred(calcularImpostoNfAccess(remessa + envio, ajusteNf));
        const adicional = arred(remessa + envio + nf);
        resultado[etapa] = {
            principal_brl: principal, imposto_remessa_brl: remessa, taxa_envio_brl: envio,
            imposto_nf_brl: nf, adicional_brl: adicional, total_brl: arred(principal + adicional),
            moeda_original: moedaLinha, cambio_aplicado: cambio,
            completo: principalOriginal <= 0 || (cambio > 0 && quantidadeValida && taxaValida && percentualValido),
            quantidade_envios: config.quantidade_envios, taxa_envio_unitaria_brl: config.taxa_envio_brl,
            percentual_remessa: config.percentual_remessa, ...camposAjusteNf(ajusteNf),
        };
    });
    return resultado;
}

// Exibição (card e Resumo): contrato v2 B — soma com precisão total; a tela formata com 2 casas.
export function resumirRemessaExteriorEstimativa(orcamento = {}, fornecedorAccess = null) {
    // pre/neg = encargos (adicionais); principais = principal BRL só das linhas pagas no exterior.
    const resumo = { pre: 0, neg: 0, principais: { pre: 0, neg: 0 }, quantidade_linhas: 0, completo: true };
    (orcamento.orcamentoitem || []).forEach((grupo) => (grupo.subitem || []).forEach((linha) => {
        // Opção (D-0113): não vai ser paga no exterior; a linha mostra a estimativa dela, o resumo não.
        if (ehSomenteVisualizacao(linha)) return;
        const calculado = calcularRemessaExteriorEstimativa(linha, orcamento, fornecedorAccess, { exato: true });
        if (!calculado.elegivel) return;
        resumo.quantidade_linhas += 1;
        Object.keys(ETAPAS_REMESSA).forEach((etapa) => {
            resumo[etapa] += calculado[etapa].adicional_brl;
            resumo.principais[etapa] += calculado[etapa].principal_brl;
            resumo.completo = resumo.completo && calculado[etapa].completo;
        });
    }));
    return resumo;
}

// Card "Estimativa de pagamento no exterior": os pagamentos no exterior (principal das linhas
// Exterior + encargos) ficam à parte do total do orçamento, que também soma as linhas Brasil.
// totalOrcamentoApiBrl: total autoritativo da API (totalEstimado*BRL), que já inclui o Imposto NF
// Access de cada grupo; sem ele (tela em edição), soma o subtotal convertido da grade + encargos.
export function pagamentosExteriorEtapa(resumo = {}, etapa = 'pre', subtotalOrcamentoBrl = 0, totalOrcamentoApiBrl = null) {
    // Contrato v2 B: soma sem arredondar os passos; arredonda só o valor exibido.
    const principalBruto = numero(resumo.principais?.[etapa]) || 0;
    const encargosBruto = numero(resumo[etapa]) || 0;
    const totalApi = numero(totalOrcamentoApiBrl);
    return { principal: arredondarBrl(principalBruto), encargos: arredondarBrl(encargosBruto), total: arredondarBrl(principalBruto + encargosBruto),
        totalOrcamento: totalApi !== null ? arredondarBrl(totalApi) : arredondarBrl((numero(subtotalOrcamentoBrl) || 0) + encargosBruto) };
}

export function alterarConfiguracaoRemessaLinha(orcamento, grupoIndex, linhaIndex, etapa, campo, valor) {
    if (!ETAPAS_REMESSA[etapa] || !['quantidade_envios', 'taxa_envio_brl', 'percentual_remessa'].includes(campo)) throw new Error('Configuração de remessa inválida.');
    return { ...orcamento, orcamentoitem: orcamento.orcamentoitem.map((grupo, g) => g !== grupoIndex ? grupo : {
        ...grupo, subitem: grupo.subitem.map((linha, l) => l !== linhaIndex ? linha : {
            ...linha, remessa_estimativa: { ...linha.remessa_estimativa,
                [etapa]: { ...configuracaoRemessaEtapa(linha, etapa), [campo]: ausente(valor) ? null : numero(valor) },
            },
        }),
    }) };
}

export const ROTULO_ETAPA_REMESSA = { pre: 'Pré-evento', neg: 'Negociado' };

// Item 13: pendências localizadas (grupo/linha/etapa/campo) de TODAS as linhas e etapas, para o
// alerta de "dados internacionais" listar cada uma com o seu "Mostrar campo". Regra inalterada:
// por linha e etapa, reporta o primeiro campo inválido (envios → taxa → percentual).
export function listarPendenciasRemessaExterior(orcamento = {}, fornecedorAccess = null) {
    const pendencias = [];
    (orcamento.orcamentoitem || []).forEach((grupo, grupoIndex) => (grupo?.subitem || []).forEach((linha, linhaIndex) => {
        if (!linhaElegivelRemessaExterior(linha, fornecedorAccess)) return;
        Object.entries(ETAPAS_REMESSA).forEach(([etapa, momento]) => {
            let principal = numero(linha[`total${momento}`]) || 0;
            if (Object.prototype.hasOwnProperty.call(linha, `valorUnitario${momento}`)) {
                const bruto = (numero(linha[`valorUnitario${momento}`]) || 0)
                    * (numero(linha[`quantidade${momento}`]) || 0)
                    * (numero(linha[`diaria${momento}`]) || 0);
                const servico = (numero(linha[`servico${momento}`]) || 0) / 100;
                const feeEspecial = !linha.remessaServicoOrdinarioCanonico && FeeIntelligence.isAgencyFeeRuleItem(linha.produto || {});
                principal = feeEspecial ? bruto * servico : bruto * (1 + servico);
            }
            if (arredondarBrl(principal) <= 0) return;
            const config = configuracaoRemessaEtapa(linha, etapa);
            const local = `${identificarLinhaOrcamento(grupo, linhaIndex, linha, 'Serviço no exterior')} (${ROTULO_ETAPA_REMESSA[etapa]})`;
            const alvo = (campo, texto) => pendencias.push({ mensagem: `${local}: ${texto}`, grupoIndex, linhaIndex, etapa, campo });
            if (config.quantidade_envios === null || !Number.isSafeInteger(config.quantidade_envios) || config.quantidade_envios < 0) alvo('quantidade_envios', 'informe a quantidade de envios (inteiro, inclusive 0).');
            else if (config.taxa_envio_brl === null || config.taxa_envio_brl < 0) alvo('taxa_envio_brl', 'informe uma taxa por envio em reais válida.');
            else if (config.percentual_remessa === null || config.percentual_remessa < 0) alvo('percentual_remessa', 'informe um percentual de remessa válido.');
        });
    }));
    return pendencias;
}

export function validarRemessaExteriorEstimativa(orcamento = {}, fornecedorAccess = null) {
    return listarPendenciasRemessaExterior(orcamento, fornecedorAccess)[0]?.mensagem ?? null;
}
